// Market history adapters.
// Demo series is only generated when the server marks coverage as "demo" / fixture.
// Live analyzer must never receive another product's chart from a silent fallback.

const OBSERVATION_HOURS = 6;

export function generateMarketHistory(now = new Date()) {
  const N = 181 * (24 / OBSERVATION_HOURS) + 1;
  const start = new Date(now.getTime() - (N - 1) * OBSERVATION_HOURS * 3600 * 1000);
  return Array.from({ length: N }, (_, i) => {
    const d = new Date(start.getTime() + i * OBSERVATION_HOURS * 3600 * 1000);
    const day = i / (24 / OBSERVATION_HOURS);
    const wave = Math.sin(day / 12), fast = Math.sin(day / 4.8);
    const marketGap = day >= 72 && day <= 78;
    const walmartOut = day >= 40 && day <= 52;
    const units = Math.round(980 + day * 1.35 + wave * 115 + fast * 35);

    if (marketGap) {
      return {
        timestamp: d,
        featuredOffer: null, lowestWfs: null, lowestSellerFulfilled: null, walmartRetailPrice: null,
        sourceCost: +(11.7 + (day > 130 ? 0.55 : 0)).toFixed(2),
        estimatedMonthlyUnits: units, demandLow: Math.round(units * 0.88), demandHigh: Math.round(units * 1.12),
        totalOffers: null, sellerCount: null, wfsOffers: null, walmartRetailInStock: false,
        gapReason: "Marketplace observation unavailable", dataQuality: "missing",
      };
    }

    return {
      timestamp: d,
      featuredOffer: +(20.2 + wave * 0.9 + (day > 115 ? -1.15 : 0) + (day > 150 ? 0.65 : 0)).toFixed(2),
      lowestWfs: +(19.55 + wave * 0.7 + (day > 115 ? -0.6 : 0)).toFixed(2),
      lowestSellerFulfilled: +(19.05 + wave * 0.6 + (day > 115 ? -0.5 : 0)).toFixed(2),
      walmartRetailPrice: walmartOut ? null : +(20.88 + (day > 90 ? -1.4 : 0)).toFixed(2),
      sourceCost: +(11.7 + (day > 130 ? 0.55 : 0)).toFixed(2),
      estimatedMonthlyUnits: units,
      demandLow: Math.round(units * 0.88),
      demandHigh: Math.round(units * 1.12),
      totalOffers: Math.max(4, Math.round(10 + wave * 2 + (day > 112 ? -2 : 0))),
      sellerCount: Math.max(3, Math.round(8 + wave * 1.4 + (day > 112 ? -1 : 0))),
      wfsOffers: Math.max(2, Math.round(5 + fast * 0.9)),
      walmartRetailInStock: !walmartOut,
      gapReason: walmartOut ? "Walmart listing temporarily out of stock" : null,
      dataQuality: "observed",
    };
  });
}

/** Map backend PRODUCT.history series points → chart records. */
export function mapHistorySeries(series = []) {
  return series.map((p) => ({
    timestamp: new Date(p.collected_at || p.timestamp),
    featuredOffer: p.buy_box ?? p.featuredOffer ?? null,
    lowestWfs: p.lowest_wfs ?? p.lowestWfs ?? null,
    lowestSellerFulfilled: p.lowest_mfn ?? p.lowestSellerFulfilled ?? null,
    walmartRetailPrice: p.walmart_price ?? p.walmartRetailPrice ?? null,
    sourceCost: p.source_cost ?? p.sourceCost ?? null,
    estimatedMonthlyUnits: p.est_units ?? p.estimatedMonthlyUnits ?? null,
    demandLow: p.demand_low ?? p.demandLow ?? null,
    demandHigh: p.demand_high ?? p.demandHigh ?? null,
    totalOffers: p.total_offers ?? p.totalOffers ?? null,
    sellerCount: p.seller_count ?? p.sellerCount ?? null,
    wfsOffers: p.wfs_count ?? p.wfsOffers ?? null,
    walmartRetailInStock: p.walmart_present != null
      ? Boolean(p.walmart_present)
      : p.walmartRetailInStock ?? null,
    gapReason: p.provenance === "unavailable" ? "Observation gap" : null,
    dataQuality: p.provenance || p.dataQuality || "observed",
    source: p.source || p.provider || null,
    provider: p.provider || p.source || null,
  }));
}

/**
 * Adapt a backend history payload to Market History records.
 *
 * CRITICAL: never silently inject demo series for a live lookup.
 * Demo generation only when coverage is "demo" (bare analyzer).
 */
export function adaptMarketHistory(apiHistory) {
  if (apiHistory == null) {
    return { records: [], demo: false, loading: true, unavailable: false };
  }

  const reason = apiHistory.reason || apiHistory.coverage;
  const explicitUnavailable =
    apiHistory.available === false ||
    reason === "history_unavailable" ||
    apiHistory.coverage === "unavailable";

  // Persisted empty (item known, zero observations) — compact empty, not “API unavailable”.
  if (
    apiHistory.coverage === "empty" ||
    reason === "no_observations" ||
    reason === "item_id_required"
  ) {
    return {
      records: [],
      demo: false,
      unavailable: true,
      emptyObserved: true,
      reason: reason || "no_observations",
      message:
        apiHistory.message ||
        "No saved Walmart price observations for this item yet.",
    };
  }

  if (explicitUnavailable) {
    return {
      records: [],
      demo: false,
      unavailable: true,
      reason: apiHistory.reason || "history_unavailable",
      message:
        apiHistory.message ||
        "Price and competition history is not available from Walmart Marketplace search.",
    };
  }

  if (Array.isArray(apiHistory.records) && apiHistory.records.length) {
    return {
      records: apiHistory.records.map((r) => ({
        ...r,
        timestamp: new Date(r.timestamp || r.collected_at),
      })),
      demo: false,
      unavailable: false,
    };
  }

  if (Array.isArray(apiHistory.series) && apiHistory.series.length) {
    return {
      records: mapHistorySeries(apiHistory.series),
      demo: apiHistory.coverage === "demo",
      unavailable: false,
      stats: apiHistory.stats || null,
      seriesAvailability: apiHistory.series_availability || null,
    };
  }

  // Bare demo path: empty series but marked demo → generate rich chart demo.
  if (apiHistory.coverage === "demo" || apiHistory.data_mode === "fixture") {
    return { records: generateMarketHistory(), demo: true, unavailable: false };
  }

  // Empty live payload — honest empty, no substitute product history.
  return {
    records: [],
    demo: false,
    unavailable: true,
    reason: apiHistory.reason || "history_unavailable",
    message:
      apiHistory.message ||
      "No historical observations for this product.",
  };
}
